import { useAnalyticsCardBase } from '../../../hooks/useAnalyticsCardBase'
import { useAnalyticsParams } from '../../../modules/AnalyticsParams/hooks/useAnalyticsParams'
import { createAnalyticsChartData } from '../../../utils/createAnalyticsChartData'
import { createChartXAxisFormatter } from '../../../utils/createChartXAxisFormatter'
import { getSummaryValue } from '../../../utils/getSummaryValue'
import type { LineChartReportCardProps } from '../AnalyticsDashboard.types'

type UseLineChartReportCardOptions = {
  reportId: string
  headerLabel?: string
  headerTooltipTitle?: string
  headerTooltipDescription?: string
  headerTooltipFormula?: string
  filters?: Record<string, string>
  overrides?: Partial<LineChartReportCardProps>
}

export const useLineChartReportCard = (
  options: UseLineChartReportCardOptions,
): LineChartReportCardProps => {
  const {
    reportId,
    headerLabel,
    headerTooltipTitle,
    headerTooltipDescription,
    headerTooltipFormula,
    filters,
    overrides,
  } = options
  const analyticsCardBase = useAnalyticsCardBase({ reportId, filters })
  const { precision, dateRange } = useAnalyticsParams()

  const base: LineChartReportCardProps = {
    ...analyticsCardBase,
    type: 'lineChart',
    value: getSummaryValue({
      reportData: analyticsCardBase.reportData,
      columnKey: analyticsCardBase.activeColumn?.key,
    }),
    data: createAnalyticsChartData({
      reportData: analyticsCardBase.reportData,
      activeSeries: analyticsCardBase.activeColumn?.key || '',
    }),
    formatXAxis: createChartXAxisFormatter({
      columnType: analyticsCardBase.reportDefinition?.columns[0].type,
      dateRange: dateRange.value.dates,
      precision: precision.value.value,
    }),
    header: {
      ...analyticsCardBase.header,
      ...(headerLabel !== undefined && { label: headerLabel }),
      ...(headerTooltipDescription !== undefined && {
        tooltip: {
          ...(headerTooltipTitle !== undefined && { title: headerTooltipTitle }),
          description: headerTooltipDescription,
          formula: headerTooltipFormula,
        },
      }),
    },
  }

  return overrides ? { ...base, ...overrides } : base
}
